import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Divider, Paper, Stack } from '@mui/material';
import {
    BookingFinancials,
    BookingProductOption,
    BookingProductPricing,
    convertNameDtoToFullName,
    TransactionType,
    useDiscardBookingTransaction,
    PaxData,
    Transaction,
    DiscountType,
    useUpdateBookingDiscount,
    EventStatuses,
} from '@travelity/api';
import { Card, IconButton, LoadingOverlay, Tag } from '@travelity/ui';
import { formatMoney, capitalizeFirstLetter } from '@travelity/utils';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import UTurnLeftIcon from '@mui/icons-material/UTurnLeft';

import { useToast } from '../../hooks';
import { Select, TextField, useForm } from '@travelity/form';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

import { PriceItem } from '../price-summary/components/price-item';
import { ReasonModal } from '../booking-item/components/reason-modal';
import { SingleLineKeyValue } from '../booking-participants/components/single-line-key-value';
import { Summary } from './components/summary';
import financialInformationSchema from './booking-financial-information.schema';
import { useSuperUser } from '../../contexts/user';

export interface ProductDetailsFormProps {
    transactions: Transaction[];
    financials: BookingFinancials;
    pricing: BookingProductPricing;
    options?: BookingProductOption[];
    readonly?: boolean;
    bookingId: string;
    eventStatus?: EventStatuses;
    preview?: boolean;
    refetch: () => void;
    pax: PaxData;
}

const BookingFinancialInformationForm: React.FC<ProductDetailsFormProps> = ({
    transactions,
    financials,
    pricing,
    options = [],
    preview,
    eventStatus,
    bookingId,
    refetch,
    readonly,
    pax,
}) => {
    const { t } = useTranslation('common');
    const [discardTransaction, setDiscardTransaction] = useState<
        Transaction | undefined
    >();
    const { showError, showSuccess } = useToast();
    const currency = pricing.currency.abbr;

    // const mainPrice = priceDtoToPrice(pricing.price, pax);
    const optionsWithPrices = options?.filter(o => o.pricing);
    // const optionPrices = options
    //     ?.filter(o => o.pricing)
    //     .map(option => ({
    //         name: option.name,
    //         type: option.type,
    //         price: priceDtoToPrice(
    //             option.pricing?.price as FlatPricingData | AgebandPricingData,
    //             option.pax
    //         ),
    //     }));

    const finalPrice =
        financials.total || financials.total === 0
            ? `${formatMoney(financials.total, currency)}`
            : '';

    const { mutate: discard } = useDiscardBookingTransaction({
        onSuccess: () => {
            setDiscardTransaction(undefined);
            refetch();
            if (discardTransaction) {
                showSuccess(
                    discardTransaction.type === TransactionType.PAYMENT
                        ? 'DISCARD_PAYMENT_SUCCESS'
                        : discardTransaction.type === TransactionType.REFUND
                        ? 'DISCARD_REFUND_SUCCESS'
                        : 'DISCARD_WAIVER_SUCCESS',
                    {
                        amount: discardTransaction.amount,
                        currency: currency,
                    }
                );
            }
        },
        onError: () => {
            if (discardTransaction) {
                showError(
                    discardTransaction.type === TransactionType.PAYMENT
                        ? 'DISCARD_PAYMENT_FAILURE'
                        : discardTransaction.type === TransactionType.REFUND
                        ? 'DISCARD_REFUND_FAILURE'
                        : 'DISCARD_WAIVER_FAILURE',
                    {
                        amount: discardTransaction.amount,
                        currency: currency,
                    }
                );
            }
        },
    });

    const prepaymentTypeOptions = useMemo(
        () => [
            { value: 'relative', label: '%' },
            {
                value: 'absolute',
                label: currency || 'Absolute',
            },
        ],
        [currency]
    );

    const [editingDiscount, setEditingDiscount] = useState(false);
    const { mutate: updateDiscount, isLoading } = useUpdateBookingDiscount({
        onSuccess: () => {
            setEditingDiscount(false);
        },
    });
    const {
        Form,
        reset,
        submit,
        formState: { isDirty },
    } = useForm({
        defaultValues: pricing.discount || {
            type: DiscountType.RELATIVE,
            amount: 0,
        },
        onSubmit: useCallback(
            (data: any) => {
                updateDiscount({
                    bookingId,
                    amount: data.amount,
                    type: data.type,
                });
            },
            [bookingId]
        ),
        mode: 'onChange',
        schema: financialInformationSchema,
    });

    const resetDefault = useCallback(() => {
        reset(
            pricing.discount || {
                type: DiscountType.RELATIVE,
                amount: 0,
            }
        );
    }, [reset, pricing]);

    useEffect(() => {
        reset(pricing.discount || { type: DiscountType.RELATIVE, amount: 0 });
    }, [pricing.discount]);

    const canEditDiscount = !readonly && eventStatus === EventStatuses.UPCOMING;
    const superUser = useSuperUser();
    const allowAction = superUser ? true : canEditDiscount;

    return (
        <>
            <Form>
                <Stack>
                    {isLoading && <LoadingOverlay />}
                    {!!pricing.price && (
                        <>
                            <Divider sx={{ mb: 1.5 }}>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={1}
                                >
                                    Main Product Price
                                    {allowAction ? (
                                        editingDiscount ? (
                                            <>
                                                <IconButton
                                                    withShadow
                                                    onClick={submit}
                                                    sx={{ m: 0 }}
                                                    icon={
                                                        <CheckIcon fontSize="small" />
                                                    }
                                                    color={
                                                        isDirty
                                                            ? 'success.main'
                                                            : 'grey.400'
                                                    }
                                                    hoverColor="success.dark"
                                                    tooltip={
                                                        isDirty
                                                            ? 'Save'
                                                            : undefined
                                                    }
                                                    tooltipPlacement="bottom"
                                                />
                                                <IconButton
                                                    withShadow
                                                    onClick={() => {
                                                        resetDefault();
                                                        setEditingDiscount(
                                                            false
                                                        );
                                                    }}
                                                    color="error.main"
                                                    hoverColor="error.dark"
                                                    tooltip="Cancel"
                                                    tooltipPlacement="bottom"
                                                    sx={{ m: 0 }}
                                                    icon={
                                                        <CloseIcon fontSize="small" />
                                                    }
                                                />
                                            </>
                                        ) : (
                                            <IconButton
                                                withShadow
                                                onClick={() =>
                                                    setEditingDiscount(true)
                                                }
                                                sx={{ m: 0 }}
                                                icon={
                                                    <EditIcon fontSize="small" />
                                                }
                                                tooltip="Edit"
                                                tooltipPlacement="bottom"
                                            />
                                        )
                                    ) : undefined}
                                </Stack>
                            </Divider>
                            <PriceItem
                                pax={pax}
                                currency={currency || ''}
                                isLoading={false}
                                price={pricing.price}
                                priceLabelSx={{
                                    color: '#9198AA',
                                    fontSize: '14px',
                                }}
                            />
                        </>
                    )}
                    {!!optionsWithPrices?.length && (
                        <Box>
                            <Divider sx={{ mb: 1.5 }}>
                                Product Options’ Price
                            </Divider>
                            {optionsWithPrices.map(option =>
                                option.pricing ? (
                                    <>
                                        <Box
                                            sx={{
                                                mb: 1.5,
                                                color: '#6B758D',
                                                fontSize: '14px',
                                                fontWeight: '500',
                                            }}
                                        >
                                            {option.name} (
                                            {capitalizeFirstLetter(option.type)}
                                            )
                                        </Box>
                                        <PriceItem
                                            currency={currency || ''}
                                            isLoading={false}
                                            price={option.pricing.price}
                                            pax={option.pax}
                                            priceLabelSx={{
                                                color: '#9198AA',
                                                fontSize: '14px',
                                            }}
                                        />
                                    </>
                                ) : undefined
                            )}
                        </Box>
                    )}
                    {editingDiscount ? (
                        <Stack>
                            <Divider sx={{ mb: 1.5 }} />
                            <Stack
                                direction="row"
                                gap={1}
                                sx={{
                                    bgcolor: '#FFFFFF',
                                    borderRadius: '8px',
                                }}
                            >
                                <TextField
                                    label={t('Discount', { ns: 'booking' })}
                                    placeholder={t('Type Discount', {
                                        ns: 'booking',
                                    })}
                                    name="amount"
                                    sx={{ flexGrow: 2 }}
                                />
                                <Select
                                    width="140px"
                                    name="type"
                                    label=""
                                    options={prepaymentTypeOptions}
                                />
                            </Stack>
                        </Stack>
                    ) : (
                        <Box>
                            {!!pricing.discount &&
                                !!financials.total &&
                                !!financials.subtotal && (
                                    <>
                                        <Divider sx={{ mb: 1.5 }}>
                                            Summary
                                        </Divider>
                                        <Summary
                                            currency={currency || ''}
                                            before={financials.subtotal}
                                            after={financials.total}
                                            discountAmount={
                                                pricing.discount.amount
                                            }
                                            discountType={pricing.discount.type}
                                            isLoading={false}
                                            showDiscountAmount
                                        />
                                    </>
                                )}
                        </Box>
                    )}

                    <Divider sx={{ my: 1.5 }} />
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        sx={{ mt: 0.5, mb: 1.5 }}
                    >
                        <Box
                            sx={{
                                color: '#6B748C',
                                fontSize: '15px',
                                fontWeight: '500',
                            }}
                        >
                            {financials.total !== financials.subtotal
                                ? 'Total after discount'
                                : 'Total'}
                        </Box>
                        <Box
                            sx={{
                                color: '#2B395B',
                                textAlign: 'right',
                                fontSize: '15px',
                                fontWeight: '600',
                            }}
                        >
                            {finalPrice}
                        </Box>
                    </Stack>
                </Stack>
            </Form>
            {preview ? <Stack spacing={2} /> : <Stack spacing={3} />}
            {!!transactions?.length && (
                <>
                    <Divider sx={{ mb: 0.5 }}>{t('Transactions')}</Divider>

                    {!!financials?.total && (
                        <SingleLineKeyValue
                            label={t('Total Amount Paid', { ns: 'booking' })}
                            darkValue
                            value={formatMoney(
                                financials?.total,
                                pricing.currency.abbr
                            )}
                        />
                    )}
                    {!!financials.debt && (
                        <SingleLineKeyValue
                            label={t('Total Dept', { ns: 'booking' })}
                            darkValue
                            value={formatMoney(
                                financials.debt,
                                pricing.currency.abbr
                            )}
                        />
                    )}
                    {!!financials?.refunded && (
                        <SingleLineKeyValue
                            label={t('Total Refunded', { ns: 'booking' })}
                            darkValue
                            value={formatMoney(
                                financials?.refunded,
                                pricing.currency.abbr
                            )}
                        />
                    )}
                    <Paper
                        sx={{
                            mt: 2,
                            p: 2,
                            height: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        {transactions?.map(transaction => (
                            <Card
                                buttons={
                                    <IconButton
                                        icon={
                                            <UTurnLeftIcon
                                                sx={{
                                                    transform: 'rotate(90deg)',
                                                }}
                                            />
                                        }
                                        withShadow
                                        tooltip="Discard"
                                        tooltipPlacement="bottom"
                                        disabled={!!transaction.discarded}
                                        onClick={() => {
                                            setDiscardTransaction(transaction);
                                        }}
                                    />
                                }
                            >
                                <Box
                                    sx={{
                                        py: '22px',
                                        px: 2,
                                        color: '#9298ab',
                                        bgcolor: '#f7f8fa',
                                        height: '36px',
                                        fontWeight: 600,
                                        borderRadius: '12px 12px 0px 0px',
                                        lineHeight: '10px',
                                        fontSize: '14px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: transaction.discarded
                                            ? 'space-between'
                                            : 'flex-end',
                                    }}
                                >
                                    {transaction.discarded && (
                                        <svg
                                            width="85"
                                            height="18"
                                            viewBox="0 0 85 18"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <rect
                                                width="85"
                                                height="18"
                                                rx="6"
                                                fill="#EF5E6F"
                                                fillOpacity="0.2"
                                            />
                                            <path
                                                d="M9.372 14V4.2H13.208C13.908 4.2 14.538 4.31667 15.098 4.55C15.6673 4.78333 16.1527 5.11933 16.554 5.558C16.9553 5.99667 17.2633 6.51467 17.478 7.112C17.6927 7.70933 17.8 8.372 17.8 9.1C17.8 9.828 17.6927 10.4953 17.478 11.102C17.2633 11.6993 16.9553 12.2173 16.554 12.656C16.162 13.0853 15.6813 13.4167 15.112 13.65C14.5427 13.8833 13.908 14 13.208 14H9.372ZM10.884 12.698L10.842 12.544H13.138C13.6327 12.544 14.0713 12.4647 14.454 12.306C14.846 12.1473 15.1727 11.9233 15.434 11.634C15.7047 11.3353 15.91 10.9713 16.05 10.542C16.19 10.1127 16.26 9.632 16.26 9.1C16.26 8.568 16.19 8.092 16.05 7.672C15.91 7.24267 15.7047 6.87867 15.434 6.58C15.1633 6.28133 14.8367 6.05267 14.454 5.894C14.0713 5.73533 13.6327 5.656 13.138 5.656H10.8L10.884 5.53V12.698ZM19.7187 14V6.65H21.1607V14H19.7187ZM20.4187 5.026C20.1107 5.026 19.8727 4.94667 19.7047 4.788C19.5367 4.62933 19.4527 4.40533 19.4527 4.116C19.4527 3.84533 19.5367 3.626 19.7047 3.458C19.882 3.29 20.12 3.206 20.4187 3.206C20.7267 3.206 20.9647 3.28533 21.1327 3.444C21.3007 3.60267 21.3847 3.82667 21.3847 4.116C21.3847 4.38667 21.296 4.606 21.1187 4.774C20.9507 4.942 20.7173 5.026 20.4187 5.026ZM25.8271 14.14C25.2111 14.14 24.6558 14.0327 24.1611 13.818C23.6665 13.6033 23.2558 13.286 22.9291 12.866L23.9091 12.026C24.1891 12.3527 24.4925 12.5907 24.8191 12.74C25.1551 12.88 25.5331 12.95 25.9531 12.95C26.1211 12.95 26.2751 12.9313 26.4151 12.894C26.5645 12.8473 26.6905 12.782 26.7931 12.698C26.9051 12.614 26.9891 12.516 27.0451 12.404C27.1011 12.2827 27.1291 12.152 27.1291 12.012C27.1291 11.7693 27.0405 11.5733 26.8631 11.424C26.7698 11.3587 26.6205 11.2887 26.4151 11.214C26.2191 11.13 25.9625 11.046 25.6451 10.962C25.1038 10.822 24.6605 10.6633 24.3151 10.486C23.9698 10.3087 23.7038 10.108 23.5171 9.884C23.3771 9.70667 23.2745 9.51533 23.2091 9.31C23.1438 9.09533 23.1111 8.862 23.1111 8.61C23.1111 8.302 23.1765 8.022 23.3071 7.77C23.4471 7.50867 23.6338 7.28467 23.8671 7.098C24.1098 6.902 24.3898 6.75267 24.7071 6.65C25.0338 6.54733 25.3791 6.496 25.7431 6.496C26.0885 6.496 26.4291 6.54267 26.7651 6.636C27.1105 6.72933 27.4278 6.86467 27.7171 7.042C28.0065 7.21933 28.2491 7.42933 28.4451 7.672L27.6191 8.582C27.4418 8.40467 27.2458 8.25067 27.0311 8.12C26.8258 7.98 26.6158 7.87267 26.4011 7.798C26.1865 7.72333 25.9905 7.686 25.8131 7.686C25.6171 7.686 25.4398 7.70467 25.2811 7.742C25.1225 7.77933 24.9871 7.83533 24.8751 7.91C24.7725 7.98467 24.6931 8.078 24.6371 8.19C24.5811 8.302 24.5531 8.428 24.5531 8.568C24.5625 8.68933 24.5905 8.806 24.6371 8.918C24.6931 9.02067 24.7678 9.10933 24.8611 9.184C24.9638 9.25867 25.1178 9.338 25.3231 9.422C25.5285 9.506 25.7898 9.58533 26.1071 9.66C26.5738 9.78133 26.9565 9.91667 27.2551 10.066C27.5631 10.206 27.8058 10.3693 27.9831 10.556C28.1698 10.7333 28.3005 10.9387 28.3751 11.172C28.4498 11.4053 28.4871 11.6667 28.4871 11.956C28.4871 12.376 28.3658 12.754 28.1231 13.09C27.8898 13.4167 27.5725 13.6733 27.1711 13.86C26.7698 14.0467 26.3218 14.14 25.8271 14.14ZM33.1984 14.14C32.517 14.14 31.9057 13.972 31.3644 13.636C30.8324 13.3 30.4077 12.8427 30.0904 12.264C29.7824 11.6853 29.6284 11.0367 29.6284 10.318C29.6284 9.59933 29.7824 8.95067 30.0904 8.372C30.4077 7.79333 30.8324 7.336 31.3644 7C31.9057 6.664 32.517 6.496 33.1984 6.496C33.8517 6.496 34.4444 6.63133 34.9764 6.902C35.5177 7.16333 35.9284 7.52267 36.2084 7.98L35.4104 8.96C35.261 8.74533 35.0697 8.54933 34.8364 8.372C34.603 8.19467 34.3557 8.05467 34.0944 7.952C33.833 7.84933 33.581 7.798 33.3384 7.798C32.8904 7.798 32.489 7.91 32.1344 8.134C31.789 8.34867 31.5137 8.64733 31.3084 9.03C31.103 9.41267 31.0004 9.842 31.0004 10.318C31.0004 10.794 31.1077 11.2233 31.3224 11.606C31.537 11.9793 31.8217 12.278 32.1764 12.502C32.531 12.726 32.923 12.838 33.3524 12.838C33.6044 12.838 33.847 12.796 34.0804 12.712C34.323 12.628 34.5564 12.502 34.7804 12.334C35.0044 12.166 35.2144 11.9607 35.4104 11.718L36.2084 12.698C35.9097 13.118 35.4804 13.4633 34.9204 13.734C34.3697 14.0047 33.7957 14.14 33.1984 14.14ZM40.3965 14.14C39.7899 14.14 39.2345 13.972 38.7305 13.636C38.2359 13.3 37.8392 12.8427 37.5405 12.264C37.2419 11.6853 37.0925 11.032 37.0925 10.304C37.0925 9.56667 37.2419 8.91333 37.5405 8.344C37.8485 7.76533 38.2592 7.31267 38.7725 6.986C39.2952 6.65933 39.8785 6.496 40.5225 6.496C40.9052 6.496 41.2552 6.552 41.5725 6.664C41.8899 6.776 42.1652 6.93467 42.3985 7.14C42.6412 7.336 42.8372 7.56467 42.9865 7.826C43.1452 8.08733 43.2432 8.36733 43.2805 8.666L42.9585 8.554V6.65H44.4145V14H42.9585V12.25L43.2945 12.152C43.2385 12.404 43.1219 12.6513 42.9445 12.894C42.7765 13.1273 42.5572 13.3373 42.2865 13.524C42.0252 13.7107 41.7312 13.86 41.4045 13.972C41.0872 14.084 40.7512 14.14 40.3965 14.14ZM40.7745 12.81C41.2132 12.81 41.6005 12.7027 41.9365 12.488C42.2725 12.2733 42.5339 11.9793 42.7205 11.606C42.9165 11.2233 43.0145 10.7893 43.0145 10.304C43.0145 9.828 42.9165 9.40333 42.7205 9.03C42.5339 8.65667 42.2725 8.36267 41.9365 8.148C41.6005 7.93333 41.2132 7.826 40.7745 7.826C40.3452 7.826 39.9625 7.93333 39.6265 8.148C39.2999 8.36267 39.0385 8.65667 38.8425 9.03C38.6559 9.40333 38.5625 9.828 38.5625 10.304C38.5625 10.7893 38.6559 11.2233 38.8425 11.606C39.0385 11.9793 39.2999 12.2733 39.6265 12.488C39.9625 12.7027 40.3452 12.81 40.7745 12.81ZM46.4279 14V6.65H47.8839V8.96L47.7439 8.4C47.8466 8.04533 48.0193 7.72333 48.2619 7.434C48.5139 7.14467 48.7986 6.916 49.1159 6.748C49.4426 6.58 49.7786 6.496 50.1239 6.496C50.2826 6.496 50.4319 6.51 50.5719 6.538C50.7213 6.566 50.8379 6.59867 50.9219 6.636L50.5439 8.204C50.4319 8.15733 50.3059 8.12 50.1659 8.092C50.0353 8.05467 49.9046 8.036 49.7739 8.036C49.5219 8.036 49.2793 8.08733 49.0459 8.19C48.8219 8.28333 48.6213 8.41867 48.4439 8.596C48.2759 8.764 48.1406 8.96467 48.0379 9.198C47.9353 9.422 47.8839 9.66933 47.8839 9.94V14H46.4279ZM54.826 14.14C54.154 14.14 53.552 13.9767 53.02 13.65C52.4973 13.314 52.082 12.8613 51.774 12.292C51.466 11.7133 51.312 11.0553 51.312 10.318C51.312 9.58067 51.4613 8.92733 51.76 8.358C52.068 7.77933 52.4833 7.32667 53.006 7C53.5286 6.664 54.1213 6.496 54.784 6.496C55.148 6.496 55.498 6.55667 55.834 6.678C56.1793 6.79 56.4873 6.94867 56.758 7.154C57.0286 7.35 57.2386 7.56933 57.388 7.812C57.5466 8.04533 57.626 8.28333 57.626 8.526L57.206 8.554V3.64H58.648V14H57.206V12.25H57.486C57.486 12.474 57.4113 12.698 57.262 12.922C57.1126 13.1367 56.912 13.3373 56.66 13.524C56.4173 13.7107 56.1326 13.86 55.806 13.972C55.4886 14.084 55.162 14.14 54.826 14.14ZM55.022 12.894C55.4606 12.894 55.848 12.782 56.184 12.558C56.52 12.334 56.7813 12.0307 56.968 11.648C57.164 11.256 57.262 10.8127 57.262 10.318C57.262 9.82333 57.164 9.38467 56.968 9.002C56.7813 8.61 56.52 8.302 56.184 8.078C55.848 7.854 55.4606 7.742 55.022 7.742C54.5833 7.742 54.196 7.854 53.86 8.078C53.524 8.302 53.258 8.61 53.062 9.002C52.8753 9.38467 52.782 9.82333 52.782 10.318C52.782 10.8127 52.8753 11.256 53.062 11.648C53.258 12.0307 53.524 12.334 53.86 12.558C54.196 12.782 54.5833 12.894 55.022 12.894ZM64.063 14.14C63.307 14.14 62.635 13.9813 62.047 13.664C61.4683 13.3373 61.011 12.894 60.675 12.334C60.3483 11.774 60.185 11.13 60.185 10.402C60.185 9.82333 60.2783 9.296 60.465 8.82C60.6517 8.344 60.9083 7.93333 61.235 7.588C61.571 7.23333 61.9677 6.96267 62.425 6.776C62.8917 6.58 63.3957 6.482 63.937 6.482C64.413 6.482 64.8563 6.57533 65.267 6.762C65.6777 6.93933 66.0323 7.18667 66.331 7.504C66.639 7.82133 66.8723 8.19933 67.031 8.638C67.199 9.06733 67.2783 9.53867 67.269 10.052L67.255 10.668H61.249L60.927 9.52H66.009L65.799 9.758V9.422C65.771 9.114 65.6683 8.83867 65.491 8.596C65.3137 8.35333 65.0897 8.162 64.819 8.022C64.5483 7.882 64.2543 7.812 63.937 7.812C63.433 7.812 63.0083 7.91 62.663 8.106C62.3177 8.29267 62.0563 8.57267 61.879 8.946C61.7017 9.31 61.613 9.76267 61.613 10.304C61.613 10.8173 61.7203 11.2653 61.935 11.648C62.1497 12.0213 62.453 12.3107 62.845 12.516C63.237 12.7213 63.6897 12.824 64.203 12.824C64.567 12.824 64.903 12.7633 65.211 12.642C65.5283 12.5207 65.869 12.3013 66.233 11.984L66.961 13.006C66.737 13.23 66.4617 13.426 66.135 13.594C65.8177 13.762 65.477 13.8973 65.113 14C64.7583 14.0933 64.4083 14.14 64.063 14.14ZM71.9021 14.14C71.2301 14.14 70.6281 13.9767 70.0961 13.65C69.5735 13.314 69.1581 12.8613 68.8501 12.292C68.5421 11.7133 68.3881 11.0553 68.3881 10.318C68.3881 9.58067 68.5375 8.92733 68.8361 8.358C69.1441 7.77933 69.5595 7.32667 70.0821 7C70.6048 6.664 71.1975 6.496 71.8601 6.496C72.2241 6.496 72.5741 6.55667 72.9101 6.678C73.2555 6.79 73.5635 6.94867 73.8341 7.154C74.1048 7.35 74.3148 7.56933 74.4641 7.812C74.6228 8.04533 74.7021 8.28333 74.7021 8.526L74.2821 8.554V3.64H75.7241V14H74.2821V12.25H74.5621C74.5621 12.474 74.4875 12.698 74.3381 12.922C74.1888 13.1367 73.9881 13.3373 73.7361 13.524C73.4935 13.7107 73.2088 13.86 72.8821 13.972C72.5648 14.084 72.2381 14.14 71.9021 14.14ZM72.0981 12.894C72.5368 12.894 72.9241 12.782 73.2601 12.558C73.5961 12.334 73.8575 12.0307 74.0441 11.648C74.2401 11.256 74.3381 10.8127 74.3381 10.318C74.3381 9.82333 74.2401 9.38467 74.0441 9.002C73.8575 8.61 73.5961 8.302 73.2601 8.078C72.9241 7.854 72.5368 7.742 72.0981 7.742C71.6595 7.742 71.2721 7.854 70.9361 8.078C70.6001 8.302 70.3341 8.61 70.1381 9.002C69.9515 9.38467 69.8581 9.82333 69.8581 10.318C69.8581 10.8127 69.9515 11.256 70.1381 11.648C70.3341 12.0307 70.6001 12.334 70.9361 12.558C71.2721 12.782 71.6595 12.894 72.0981 12.894Z"
                                                fill="#3B4D7D"
                                            />
                                        </svg>
                                    )}
                                    <Tag
                                        label={t('Type', { ns: 'booking' })}
                                        values={[
                                            transaction.type ===
                                            TransactionType.PAYMENT
                                                ? 'Payment'
                                                : transaction.type ===
                                                  TransactionType.REFUND
                                                ? 'Refund'
                                                : 'Waive',
                                        ]}
                                    />
                                </Box>
                                <Stack gap={1} sx={{ p: 2 }}>
                                    <SingleLineKeyValue
                                        dark
                                        label={t('Operator', { ns: 'booking' })}
                                        value={transaction.operator}
                                    />
                                    <SingleLineKeyValue
                                        dark
                                        label="Date/Time"
                                        value={format(
                                            transaction.createdDate,
                                            'dd MMM yyyy, HH:mm'
                                        )}
                                    />
                                    <Divider />
                                    <SingleLineKeyValue
                                        dark
                                        label={`${
                                            transaction.type ===
                                            TransactionType.PAYMENT
                                                ? 'Payment'
                                                : transaction.type ===
                                                  TransactionType.REFUND
                                                ? 'Refund'
                                                : 'Waive'
                                        } method`}
                                        value={t(transaction.method)}
                                    />
                                    <SingleLineKeyValue
                                        dark
                                        label={t('Amount paid', {
                                            ns: 'booking',
                                        })}
                                        value={formatMoney(
                                            transaction.amount,
                                            currency
                                        )}
                                    />
                                    {transaction.discarded && (
                                        <>
                                            <Divider sx={{ mt: 1 }}>
                                                Discarding details
                                            </Divider>
                                            {transaction.discarded.message && (
                                                <>
                                                    <SingleLineKeyValue
                                                        dark
                                                        label={t('Reason', {
                                                            ns: 'booking',
                                                        })}
                                                        value={
                                                            transaction
                                                                .discarded
                                                                .message
                                                        }
                                                    />
                                                    <Divider />
                                                </>
                                            )}
                                            <SingleLineKeyValue
                                                dark
                                                label={t('Operator', {
                                                    ns: 'booking',
                                                })}
                                                value={convertNameDtoToFullName(
                                                    transaction.discarded.name
                                                )}
                                            />
                                            <SingleLineKeyValue
                                                dark
                                                label="Date/Time"
                                                value={format(
                                                    transaction.discarded.date,
                                                    'dd MMM yyyy, HH:mm'
                                                )}
                                            />
                                        </>
                                    )}
                                </Stack>
                            </Card>
                        ))}
                    </Paper>
                </>
            )}
            <ReasonModal
                title="Are you sure you want to discard this payment?"
                content="Provide the reason for discarding this payment"
                confirmText="Confirm"
                open={!!discardTransaction}
                handleCancel={() => setDiscardTransaction(undefined)}
                handleConfirm={reason => {
                    discard({
                        bookingId: bookingId as string,
                        transactionId: discardTransaction?.id as string,
                        message: reason,
                    });
                }}
            />
        </>
    );
};

export default React.memo(BookingFinancialInformationForm);
